﻿import { BaseEditor } from './base-editor';
import { IForm } from '../interface/form';
import { AsyncValidatorFn, ValidatorFn } from '../types/validator-fn';
import { IEditor } from '../interface/editor';
import { localeInfo } from '../../locale';
import { convertToDate, convertToJsonDate, isDateInArray } from '../../utils/date';

export const CalendarEditorTagName = 't-calendar';

interface ILocaleNames {
    shortName: string,
    fullName: string
}

interface IDayItem {
    title: string,
    value: Date,
    /**
     * this is the original month when generate the bindable data.
     * Please use value.getMonth() for actual month
     */
    month: number,
    /**
     * this is the original year when generate the bindable data.
     * Please use value.getFullYear() for actual month
     */
    year: number,
    sameMonth: boolean,
    disabled: boolean,
    selected: boolean,
    today: boolean
}

interface IMonthItem {
    title: string,
    month: number,
    year: number,
    disabled: boolean
}

interface IYearItem {
    year: number,
    disabled: boolean
}

interface IDecadeItem {
    fromYear: number,
    toYear: number,
    disabled: boolean
}

export enum CalendarViewModel {
    days = 0,
    months = 1,
    years = 2,
    decades = 3
}

export function createCalendarEditor(form: IForm,
    name: string,
    element: HTMLElement,
    validators: ValidatorFn[],
    asyncValidators: AsyncValidatorFn[]): IEditor {
    return new TCalendarEditor(form, name, element, validators, asyncValidators);
}

export class TCalendarEditor extends BaseEditor<string | string[]> {
    valueElement: HTMLInputElement;
    viewContainerElement: HTMLDivElement;
    viewTitleElement: HTMLElement;

    startDate = new Date();
    startView = CalendarViewModel.days;
    minDate?: Date;
    maxDate?: Date;
    multiple = false;

    currentYear = 0;
    currentMonth = 0;//start from 0
    currentDate = 0;
    currentView: CalendarViewModel = null!;
    localeDayNames: ILocaleNames[] = [];
    localeMonthNames: ILocaleNames[] = [];
    selectedDates: Date[] = [];

    /**
     * Used to bind the grid on UI
     */
    bindableDays: IDayItem[][] = [];
    /**
     * Used to bind the grid on UI
     */
    bindableMonths: IMonthItem[][] = [];
    /**
     * Used to bind the grid on UI
     */
    bindableYears: IYearItem[][] = [];
    /**
     * Used to bind the grid on UI
     */
    bindableDecades: IDecadeItem[][] = [];

    constructor(
        form: IForm,
        name: string,
        element: HTMLElement,
        validators: ValidatorFn[],
        asyncValidators: AsyncValidatorFn[]) {
        super(form, name, element, validators, asyncValidators);
        this._initDayNames();
        this._initMonthNames();

        const vce= element.querySelector<HTMLDivElement>('.t-calendar-view');
        const ve = element.querySelector<HTMLInputElement>(`input[name='${this.name}']`);
        const vte =element.querySelector<HTMLElement>('.t-calendar-current');
        if(!vce || !ve || !vte) {
            throw new Error(`Invalid DOM of field ${name}`);
        }
        //get elements
        this.viewContainerElement = vce;
        this.valueElement = ve;
        this.viewTitleElement = vte;

        //init attributes
        const initStartView = this.getAttributeValue('start-view');
        if (initStartView) {
            this.startView = Number(initStartView) as CalendarViewModel;
        }
        this.currentView = this.startView;

        const initStartDate = this.getAttributeValue('start-date');
        if (initStartDate) {
            const initStartDateValue = convertToDate(initStartDate);
            if (initStartDateValue) {
                this.startDate = initStartDateValue;
            }
        }
        this.currentYear = this.startDate.getFullYear();
        this.currentMonth = this.startDate.getMonth();
        this.currentDate = this.startDate.getDate();

        const initMinDate = this.getAttributeValue('min-date');
        if (initMinDate) {
            const initMinDateValue = convertToDate(initMinDate);
            if (initMinDateValue) {
                this.minDate = initMinDateValue;
            }
        }

        const initMaxDate = this.getAttributeValue('max-date');
        if (initMaxDate) {
            const initMaxDateValue = convertToDate(initMaxDate);
            if (initMaxDateValue) {
                this.maxDate = initMaxDateValue;
            }
        }

        const initMultiple = this.getAttributeValue('multiple');
        if (initMultiple) {
            this.multiple = initMultiple === '1';
        }

        //bind event
        this.element.querySelector('.t-calendar-prev')!.addEventListener('click', (ev: Event) => {
            this.goToPrevious();
        });
        this.element.querySelector('.t-calendar-current')!.addEventListener('click', (ev: Event) => {
            this.switchView();
        });
        this.element.querySelector('.t-calendar-next')!.addEventListener('click', (ev: Event) => {
            this.goToNext();
        });

        //parse init value    
        this.value = this.valueElement.value.split(',');
    }

    get value(): string | string[]| null {
        if (this.multiple) {
            return this.valueElement.value.split(',');
        } else {
            return this.valueElement.value;
        }
    }
    set value(value: string | string[] | null) {
        this.selectedDates.length = 0;

        if (value != null && value !== undefined) {
            if (Array.isArray(value)) {
                if (this.multiple) {
                    value.forEach(d => {
                        const date = convertToDate(d);
                        if (date) {
                            this.selectedDates.push(date);
                            this.setCurrentDate(date);
                        }
                    });
                } else if (value.length > 0) {
                    const date = convertToDate(value[0]);
                    if (date) {
                        this.selectedDates.push(date);
                        this.setCurrentDate(date);
                    }
                }
            } else {
                const date = convertToDate(value);
                if (date) {
                    this.selectedDates.push(date);
                    this.setCurrentDate(date);
                }
            }
        }


        const parsedValues: string[] = this.selectedDates.map(d => {
            return convertToJsonDate(d)!;
        });
        this.valueElement.value = parsedValues.join(',');
        this._render();
    }

    private setCurrentDate(date: Date) {
        this.currentYear = date.getFullYear();
        this.currentMonth = date.getMonth();
        this.currentDate = date.getDate();
    }

    setDisabled(disabled: boolean): void {
        this.valueElement.disabled = disabled;
        this.setDisabledInternal(disabled);
    }


    selectDecades(decade: IDecadeItem):void {
        if (decade.disabled) {
            return;
        }
        this.currentYear = decade.fromYear + 8;
        const lastDate = new Date(this.currentYear, this.currentMonth + 1, 0);
        const lastDay = lastDate.getDate();
        if (this.currentDate > lastDay) {
            this.currentDate = lastDay;
        }
        this.currentView = CalendarViewModel.years;
        this._render();
    }

    selectYear(year: IYearItem):void {
        if (year.disabled) {
            return;
        }
        this.currentYear = year.year;
        this.currentView = CalendarViewModel.months;
        this._render();
    }

    selectMonth(month: IMonthItem):void {
        if (month.disabled) {
            return;
        }
        this.currentYear = month.year;
        this.currentMonth = month.month;
        const lastDate = new Date(this.currentYear, this.currentMonth + 1, 0);
        const lastDay = lastDate.getDate();
        if (this.currentDate > lastDay) {
            this.currentDate = lastDay;
        }
        this.currentView = CalendarViewModel.days;
        this._render();
    }

    selectDay(day: IDayItem):void {
        if (day.disabled) {
            return;
        }
        const currentIdx = isDateInArray(this.selectedDates, day.value);
        if (day.selected) {
            day.selected = false;
            if (this.multiple) {
                if (currentIdx >= 0) {
                    this.selectedDates.splice(currentIdx, 1);
                }
            } else {
                this.selectedDates.length = 0;
            }
        }
        else {
            day.selected = true;
            if (this.multiple) {
                if (currentIdx < 0) {
                    this.selectedDates.push(day.value);
                }
            } else {
                this.selectedDates.length = 0;
                this.selectedDates.push(day.value);
            }
        }

        if (this.multiple) {
            this.notifyChanges(this.formatValue(this.selectedDates));
        }
        else if (this.selectedDates.length > 0) {
            this.notifyChanges(this.formatValue(this.selectedDates[0]));
        } else {
            this.notifyChanges('');
        }

        this._render();
    }

    private formatValue(date: Date | Date[] | null): string | string[] {
        if (date == null) {
            return '';
        }
        if (Array.isArray(date)) {
            return date.map(d => {
                const v = convertToJsonDate(d);
                return v ?? '';
            });
        }
        else {
            const v = convertToJsonDate(date);
            return v ?? '';
        }
    }

    goToPrevious():void {
        if (this.currentView === CalendarViewModel.days) {
            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear--;
            }
            else {
                this.currentMonth--;
            }
            this._render();
        } else if (this.currentView === CalendarViewModel.months) {
            this.currentYear--;
            this._render();
        } else if (this.currentView === CalendarViewModel.years) {
            this.currentYear -= 16;
            this._render();
        } else if (this.currentView === CalendarViewModel.decades) {
            //16 cells, each cell has 16 years
            this.currentYear -= (16 * 16);
            this._render();
        }
    }

    switchView():void {
        if (this.currentView < CalendarViewModel.decades) {
            this.currentView++;
            this._render();
        }
    }

    goToNext():void {
        if (this.currentView === CalendarViewModel.days) {
            if (this.currentMonth < 11) {
                this.currentMonth++;
            } else {
                this.currentMonth = 0;
                this.currentYear++;
            }
            this._render();
        } else if (this.currentView === CalendarViewModel.months) {
            this.currentYear++;
            this._render();
        } else if (this.currentView === CalendarViewModel.years) {
            this.currentYear += 16;
            this._render();
        } else if (this.currentView === CalendarViewModel.decades) {
            //16 cells, each cell has 16 years
            this.currentYear += (16 * 16);
            this._render();
        }
    }

    private _initDayNames() {
        const shortDayNames = localeInfo.calendar!.shortDayNames!;
        const fullDaysNames = localeInfo.calendar!.fullDayNames!;
        for (let i = 0; i <= 6; i++) {
            this.localeDayNames.push({
                shortName: shortDayNames[i],
                fullName: fullDaysNames[i]
            });
        }
    }

    private _initMonthNames() {
        const shortMonthNames = localeInfo.calendar!.shortMonthNames!;
        const fullMonthNames = localeInfo.calendar!.fullMonthNames!;
        for (let i = 0; i <= 11; i++) {
            this.localeMonthNames.push({
                shortName: shortMonthNames[i],
                fullName: fullMonthNames[i]
            });
        }
    }

    private _render() {
        this.viewContainerElement.innerHTML = '';
        if (this.currentView === CalendarViewModel.days) {
            this._renderDays();
        } else if (this.currentView === CalendarViewModel.months) {
            this._renderMonths();
        } if (this.currentView === CalendarViewModel.years) {
            this._renderYears();
        } if (this.currentView === CalendarViewModel.decades) {
            this._renderDecades();
        }
    }

    private _renderDays() {
        this.refreshBindableDays();

        this.viewTitleElement.innerHTML = `${this.localeMonthNames[this.currentMonth].fullName} ${this.currentYear}`;

        //render days grid
        const tableElement = document.createElement('table');
        tableElement.classList.add('t-calendar-v-days');

        //thead
        const theadElement = document.createElement('thead');
        //tr
        const theadTrElement = document.createElement('tr');
        const theadThElements = this.localeDayNames.map(d => {
            //th
            const thElement = document.createElement('th');
            thElement.setAttribute('scope', 'col');
            thElement.setAttribute('title', d.fullName);
            thElement.innerHTML = d.shortName;
            return thElement;
        });
        theadTrElement.append(...theadThElements);
        theadElement.appendChild(theadTrElement);
        tableElement.appendChild(theadElement);

        //tbody
        const tbodyElement = document.createElement('tbody');
        //tr
        const tbodyTrElements = this.bindableDays.map(rowDay => {
            const tbodyTrElement = document.createElement('tr');
            const tbodyTdElements = rowDay.map(colDay => {
                const tdElement = document.createElement('td');
                if (!colDay.sameMonth) {
                    tdElement.classList.add('t-calendar-other');
                }
                if (colDay.disabled) {
                    tdElement.classList.add('t-calendar-disable');
                }
                if (colDay.today) {
                    tdElement.classList.add('t-calendar-today');
                }
                if (colDay.selected) {
                    tdElement.classList.add('t-calendar-selected');
                }
                tdElement.addEventListener('click', (ev: Event) => {
                    this.selectDay(colDay);
                });

                const aElement = document.createElement('a');
                aElement.innerHTML = colDay.title;
                tdElement.appendChild(aElement);

                return tdElement;
            });
            tbodyTrElement.append(...tbodyTdElements);

            return tbodyTrElement;
        });
        tbodyElement.append(...tbodyTrElements);
        tableElement.appendChild(tbodyElement);

        this.viewContainerElement.appendChild(tableElement);
    }

    private _renderMonths() {
        this.refreshBindableMonths();
        this.viewTitleElement.innerHTML = String(this.currentYear);

        //render month grid
        const tableElement = document.createElement('table');
        tableElement.classList.add('t-calendar-v-months');

        const tbodyElement = document.createElement('tbody');
        const trElements = this.bindableMonths.map(r => {
            const trElement = document.createElement('tr');
            const tdElements = r.map(m => {
                const tdElement = document.createElement('td');
                if (m.disabled) {
                    tdElement.classList.add('t-calendar-disable');
                }
                tdElement.addEventListener('click', (ev: Event) => {
                    this.selectMonth(m);
                });
                const aElement = document.createElement('a');
                aElement.innerHTML = m.title;
                tdElement.appendChild(aElement);

                return tdElement;
            });
            trElement.append(...tdElements);
            return trElement;
        });
        tbodyElement.append(...trElements);
        tableElement.appendChild(tbodyElement);

        this.viewContainerElement.appendChild(tableElement);
    }

    private _renderYears() {
        this.refreshBindableYears();
        const fromTo = this.getYearsFromAndTo();
        this.viewTitleElement.innerHTML = `${fromTo.from}-${fromTo.to}`;

        //render years grid
        const tableElement = document.createElement('table');
        tableElement.classList.add('t-calendar-v-years');

        const tbodyElement = document.createElement('tbody');
        const trElements = this.bindableYears.map(r => {
            const trElement = document.createElement('tr');
            const tdElements = r.map(y => {
                const tdElement = document.createElement('td');
                if (y.disabled) {
                    tdElement.classList.add('t-calendar-disable');
                }
                tdElement.addEventListener('click', (ev: Event) => {
                    this.selectYear(y);
                });
                const aElement = document.createElement('a');
                aElement.innerHTML = String(y.year);
                tdElement.appendChild(aElement);

                return tdElement;
            });
            trElement.append(...tdElements);
            return trElement;
        });
        tbodyElement.append(...trElements);
        tableElement.appendChild(tbodyElement);

        this.viewContainerElement.appendChild(tableElement);
    }

    private _renderDecades() {
        this.refreshBindableDecades();
        const fromTo = this.getDecadeFromAndToYear();
        this.viewTitleElement.innerHTML = `${fromTo.from}-${fromTo.to}`;

        //render years grid
        const tableElement = document.createElement('table');
        tableElement.classList.add('t-calendar-v-decades');

        const tbodyElement = document.createElement('tbody');
        const trElements = this.bindableDecades.map(r => {
            const trElement = document.createElement('tr');
            const tdElements = r.map(y => {
                const tdElement = document.createElement('td');
                if (y.disabled) {
                    tdElement.classList.add('t-calendar-disable');
                }
                tdElement.addEventListener('click', (ev: Event) => {
                    this.selectDecades(y);
                });
                const aElement = document.createElement('a');
                aElement.innerHTML = `${y.fromYear} - ${y.toYear}`;
                tdElement.appendChild(aElement);

                return tdElement;
            });
            trElement.append(...tdElements);
            return trElement;
        });
        tbodyElement.append(...trElements);
        tableElement.appendChild(tbodyElement);

        this.viewContainerElement.appendChild(tableElement);
    }

    //#region Days
    private refreshBindableDays() {
        const firstDayofMonth = new Date(this.currentYear, this.currentMonth, 1);
        const firstDayWeekIndex = firstDayofMonth.getDay();//0 is sunday
        const calendarFirstDay = localeInfo.calendar!.firstDayOfWeek!;
        let previousDays = 0;
        if (firstDayWeekIndex > calendarFirstDay) {
            previousDays = firstDayWeekIndex - calendarFirstDay;
        }
        else if (firstDayWeekIndex < calendarFirstDay) {
            previousDays = 7 - (calendarFirstDay - firstDayWeekIndex);
        }

        const today = new Date();

        this.bindableDays = [];

        const loopDate = new Date(firstDayofMonth.getFullYear(), firstDayofMonth.getMonth(), firstDayofMonth.getDate());
        loopDate.setDate(loopDate.getDate() - previousDays - 1);
        //6 lines
        for (let i = 0; i < 6; i++) {
            const weekDays: IDayItem[] = [];
            //each line has 7 days
            for (let j = 0; j < 7; j++) {
                loopDate.setDate(loopDate.getDate() + 1);
                weekDays.push({
                    title: String(loopDate.getDate()),
                    value: new Date(loopDate.getFullYear(), loopDate.getMonth(), loopDate.getDate()),
                    month: this.currentMonth,
                    year: this.currentYear,
                    sameMonth: loopDate.getFullYear() === this.currentYear && loopDate.getMonth() === this.currentMonth,
                    disabled: this.isDateDisabled(loopDate),
                    selected: this.isDateSelected(loopDate),
                    today: loopDate.getFullYear() === today.getFullYear() && loopDate.getMonth() === today.getMonth() && loopDate.getDate() === today.getDate()
                });
            }
            this.bindableDays.push(weekDays);
        }
    }

    private isDateDisabled(date: Date): boolean {
        if (this.disabled) {
            return true;
        }

        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        if (this.minDate &&
            dateOnly < new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate())) {
            return true;
        }
        if (this.maxDate &&
            dateOnly > new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate())) {
            return true;
        }
        return false;
    }

    private isDateSelected(date: Date): boolean {
        if (isDateInArray(this.selectedDates, date) >= 0) {
            return true;
        }
        return false;
    }
  //#endregion

    //#region Months
    private refreshBindableMonths() {
        this.bindableMonths = [];
        let monthIndex = 0;
        //3 lines
        for (let i = 0; i < 3; i++) {
            const months: IMonthItem[] = [];
            for (let j = 0; j < 4; j++) {
                //each line has 4 months
                months.push({
                    title: this.localeMonthNames[monthIndex].shortName,
                    month: monthIndex,
                    year: this.currentYear,
                    disabled: this.isMonthDisabled(this.currentYear, monthIndex)
                });
                monthIndex++;
            }
            this.bindableMonths.push(months);
        }
    }


    /**
     * 
     * @param month month index start from zero
     */
    private isMonthDisabled(year: number, month: number) {
        if (this.disabled) {
            return true;
        }

        if (this.minDate) {
            const lastDateOfMonth = new Date(year, month + 1, 0);
            if (lastDateOfMonth < new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate())) {
                return true;
            }
        }
        if (this.maxDate) {
            const firstDate = new Date(year, month, 1);
            if (firstDate > new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate())) {
                return true;
            }
        }

        return false;
    }

    //#endregion

    //#region Years

    private refreshBindableYears() {
        //totally display 16(4x4) cells
        const startYear = this.currentYear - 8;
        let yearIdx = startYear;
        this.bindableYears = [];
        for (let i = 0; i < 4; i++) {
            const years: IYearItem[] = [];
            for (let j = 0; j < 4; j++) {
                years.push({
                    year: yearIdx,
                    disabled: this.isYearDisabled(yearIdx)
                });
                yearIdx++;
            }
            this.bindableYears.push(years);
        }
    }


    private getYearsFromAndTo(): { from: number, to: number } {
        if (this.bindableYears.length > 0) {
            const fromYear = this.bindableYears[0][0].year;
            const lastRow = this.bindableYears.length - 1;
            const lastCol = this.bindableYears[lastRow].length - 1;
            const toYear = this.bindableYears[lastRow][lastCol].year;
            return {
                from: fromYear,
                to: toYear
            };
        } else {
            return {
                from: 0,
                to: 0
            };
        }
    }

    private isYearDisabled(year: number): boolean {
        if (this.disabled) {
            return true;
        }

        if (this.minDate) {
            const lastDateOfYear = new Date(year + 1, 0, 0);
            if (lastDateOfYear < new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate())) {
                return true;
            }
        }
        if (this.maxDate) {
            const firstDate = new Date(year, 0, 1);
            if (firstDate > new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate())) {
                return true;
            }
        }
        return false;
    }

    //#endregion

    //#region Decades
    private refreshBindableDecades() {
        //totally display 16(4x4) cells
        //each cell has 16 years
        const currentCellStartYear = this.currentYear - 8;

        let fromYear = currentCellStartYear - (8 * 16);
        let toYear = fromYear + 15;

        this.bindableDecades = [];

        for (let i = 0; i < 4; i++) {
            const decades: IDecadeItem[] = [];
            for (let j = 0; j < 4; j++) {
                decades.push({
                    fromYear: fromYear,
                    toYear: toYear,
                    disabled: this.isDecadeDisabled(fromYear, toYear)
                });
                fromYear += 16;
                toYear += 16;
            }
            this.bindableDecades.push(decades);
        }
    }

    private isDecadeDisabled(fromYear: number, endYear: number): boolean {
        if (this.disabled) {
            return true;
        }

        if (this.minDate) {
            const lastDateOfYear = new Date(endYear + 1, 0, 0);
            if (lastDateOfYear < new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate())) {
                return true;
            }
        }
        if (this.maxDate) {
            const firstDate = new Date(fromYear, 0, 1);
            if (firstDate > new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate())) {
                return true;
            }
        }
        return false;
    }

    private getDecadeFromAndToYear(): { from: number, to: number } {
        if (this.bindableDecades.length > 0) {
            const fromYear = this.bindableDecades[0][0].fromYear;
            const lastRow = this.bindableDecades.length - 1;
            const lastCol = this.bindableDecades[lastRow].length - 1;
            const toYear = this.bindableDecades[lastRow][lastCol].toYear;
            return {
                from: fromYear,
                to: toYear
            };
        } else {
            return {
                from: 0,
                to: 0
            };
        }
    }
  //#endregion

    setFocus():void {
        //todo
    }
}